import React from "react";
import { Icon } from "@/components/Icon";
import { formatOdds, useOddsFormat } from "@/lib/odds";

/* Shared sportsbook primitives — one premium look across the board, the event
   detail page and the bet slip. Built to the "02 Sportsbook" design system:
   dark neutral surfaces, single green #00D632 + single red #EF4444, Manrope for
   numbers, restrained depth (1px ring + faint top highlight). */

// A short, stable monogram from a team / competitor name. Decorative only.
export function initials(name = "") {
  const words = String(name).trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "—";
  if (words.length === 1) return words[0].slice(0, 3).toUpperCase();
  return words.slice(0, 3).map((w) => w[0]).join("").toUpperCase();
}

// Neutral team badge (keeps the restrained palette — no rainbow team colours).
export function TeamBadge({ name, size = 32, className = "" }) {
  const fs = Math.round(size * 0.36);
  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center rounded-lg border border-[#27272A] bg-[#1C1C21] font-display font-extrabold uppercase tracking-tight text-[#E4E4E7] ${className}`}
      style={{ width: size, height: size, fontSize: fs }}
      aria-hidden="true"
    >
      {initials(name)}
    </span>
  );
}

const SB_SIZE = {
  lg: { h: 64, price: 20, label: 11 },
  md: { h: 52, price: 17, label: 10 },
  sm: { h: 46, price: 15, label: 10 },
};

/**
 * SBOddsButton — the one premium odds button.
 *  label     small text on top (team code, "KC −2.5", "O 51.5"…) — omit for price-only
 *  price     the decimal price (number) — big Manrope
 *  selected  on the slip → solid green + check
 *  locked    market suspended → lock glyph, no price, not tappable
 *  move      "up" | "down" | null → green ▲ / red ▼ next to the price
 *  winner    settled winner → subtle green tint outline
 *  size      "lg" | "md" | "sm"
 */
export function SBOddsButton({
  label, price, selected = false, locked = false, move = null, winner = false,
  size = "md", onClick, testid, className = "",
}) {
  const s = SB_SIZE[size] || SB_SIZE.md;
  const oddsFormat = useOddsFormat();
  const priceStr = price == null ? "" : formatOdds(price, oddsFormat);
  const base = "sb-odds relative flex w-full flex-col items-center justify-center rounded-xl px-2 text-center transition-colors";
  let look;
  if (locked) {
    look = "cursor-not-allowed border border-[#1F1F23] bg-[#101013] text-[#52525b]";
  } else if (selected) {
    look = "border border-[#00D632] bg-[#00D632] text-[#0A0A0A] shadow-[0_2px_10px_rgba(0,214,50,0.25)]";
  } else if (winner) {
    look = "border border-[#00D632]/60 bg-[#00D632]/10 text-white";
  } else {
    look = "border border-[#27272A] bg-[#141417] text-white hover:border-[#00D632]/70 hover:bg-[#17171b]";
  }
  return (
    <button
      type="button"
      disabled={locked}
      onClick={onClick}
      data-testid={testid}
      style={{ height: s.h }}
      className={`${base} ${look} ${className}`}
    >
      {/* faint top highlight for depth (not on selected) */}
      {!selected && !locked && (
        <span className="pointer-events-none absolute inset-x-0 top-0 h-px rounded-t-xl bg-white/[0.05]" />
      )}
      {selected && (
        <span className="absolute right-1.5 top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-[#0A0A0A]/15">
          <Icon name="check" size={11} className="text-[#0A0A0A]" />
        </span>
      )}
      {label != null && (
        <span
          className={`mb-0.5 line-clamp-1 max-w-full font-semibold leading-none ${selected ? "text-[#0A0A0A]/70" : locked ? "text-[#52525b]" : "text-[#a1a1aa]"}`}
          style={{ fontSize: s.label }}
        >
          {label}
        </span>
      )}
      {locked ? (
        <Icon name="lock" size={16} className="text-[#52525b]" />
      ) : (
        <span className="flex items-center gap-1 font-display font-extrabold tabular-nums leading-none" style={{ fontSize: s.price }}>
          {priceStr}
          {move === "up" && <span className="text-[13px] leading-none text-[#00D632]">▲</span>}
          {move === "down" && <span className="text-[13px] leading-none text-[#EF4444]">▼</span>}
        </span>
      )}
    </button>
  );
}

/* OddsButton — the premium price button (design SBXPrice.dc.html, README §2.1).
   Variants: row (desktop board 44) · rowM (mobile board 50) · stack (54) · big (60).
   Keeps SBOddsButton's API shape: `line` (was label), `price` (decimal), `selected`,
   `locked`, `move` ("up"|"down"), `na`, `onClick`, `testid`. Price = formatOdds. */
const ODDS_VARIANT = {
  row: { h: 44, dir: "row", gap: 6, px: 10, ls: 12, ps: 15, r: 10 },
  rowM: { h: 50, dir: "column", gap: 4, px: 4, ls: 11, ps: 14, r: 10 },
  stack: { h: 54, dir: "column", gap: 5, px: 8, ls: 12, ps: 17, r: 12 },
  big: { h: 60, dir: "column", gap: 6, px: 8, ls: 12, ps: 20, r: 12 },
};
export function OddsButton({
  line = "", price, selected = false, locked = false, na = false, move = null,
  variant = "row", onClick, testid, className = "",
}) {
  const V = ODDS_VARIANT[variant] || ODDS_VARIANT.row;
  const oddsFormat = useOddsFormat();
  const priceStr = price == null ? "—" : formatOdds(price, oddsFormat);
  const isNa = na;
  const isLock = !na && locked;
  const isSel = !na && !locked && selected;
  const jc = V.dir === "row" && line ? "space-between" : "center";
  const common = {
    boxSizing: "border-box", width: "100%", height: V.h, borderRadius: V.r, display: "flex",
    flexDirection: V.dir, alignItems: "center", justifyContent: jc, gap: V.gap, padding: `0 ${V.px}px`,
  };

  if (isNa) {
    return (
      <div data-testid={testid} aria-disabled="true" title="No market posted"
        style={{ boxSizing: "border-box", width: "100%", height: V.h, borderRadius: V.r, boxShadow: "inset 0 0 0 1px #1A1A1E", display: "flex", alignItems: "center", justifyContent: "center", font: "700 14px Manrope", color: "#3F3F46" }}>—</div>
    );
  }
  if (isLock) {
    return (
      <div data-testid={testid} aria-disabled="true" title="Market suspended"
        style={{ ...common, background: "#0E0E11", boxShadow: "inset 0 0 0 1px #1F1F23", cursor: "not-allowed" }}>
        {line && <span style={{ font: `600 ${V.ls}px Outfit`, color: "#52525b", whiteSpace: "nowrap" }}>{line}</span>}
        <span style={{ width: 13, height: 13, flex: "none", background: "#52525b", WebkitMask: "url(/icons/lock.svg) center/contain no-repeat", mask: "url(/icons/lock.svg) center/contain no-repeat" }} />
      </div>
    );
  }
  const aria = `${line ? line + " " : ""}${priceStr}${isSel ? ", on bet slip" : ""}`;
  const onKey = (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick && onClick(); } };
  if (isSel) {
    return (
      <div data-testid={testid} role="button" tabIndex={0} aria-pressed="true" aria-label={aria} onClick={onClick} onKeyDown={onKey}
        className={`sb-oddsbtn-sel ${className}`}
        style={{ ...common, background: "#00D632", boxShadow: "inset 0 1px 0 rgba(255,255,255,.3),0 1px 2px rgba(0,0,0,.4)", cursor: "pointer" }}>
        {line && <span style={{ font: `700 ${V.ls}px Outfit`, color: "rgba(10,10,10,.62)", whiteSpace: "nowrap" }}>{line}</span>}
        <span style={{ font: `800 ${V.ps}px Manrope`, letterSpacing: "-.01em", color: "#0A0A0A", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{priceStr}</span>
      </div>
    );
  }
  return (
    <div data-testid={testid} role="button" tabIndex={0} aria-pressed="false" aria-label={aria} onClick={onClick} onKeyDown={onKey}
      className={`sb-oddsbtn ${className}`}
      style={{ ...common, background: "#141417", boxShadow: "inset 0 0 0 1px #26262B,inset 0 1px 0 rgba(255,255,255,.05)", cursor: "pointer", transition: "background .15s,box-shadow .15s" }}>
      {line && <span style={{ font: `600 ${V.ls}px Outfit`, color: "#8A8A93", whiteSpace: "nowrap" }}>{line}</span>}
      <span style={{ display: "flex", alignItems: "center", gap: 4, font: `800 ${V.ps}px Manrope`, letterSpacing: "-.01em", color: "#fff", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
        {priceStr}
        {move === "up" && <span style={{ width: 0, height: 0, borderLeft: "4px solid transparent", borderRight: "4px solid transparent", borderBottom: "6px solid #00D632" }} />}
        {move === "down" && <span style={{ width: 0, height: 0, borderLeft: "4px solid transparent", borderRight: "4px solid transparent", borderTop: "6px solid #EF4444" }} />}
      </span>
    </div>
  );
}

// Small uppercase market/league chip used in event cards + slip legs.
export function SBTag({ children, live = false, className = "" }) {
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.14em] text-[#71717a] ${className}`}>
      {live && <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#EF4444]" />}
      {children}
    </span>
  );
}

// Red LIVE pill (event cards / detail header). With a `count` it renders the
// broadcast-scoreboard soft-bordered pill (bg tint + inset red ring + pulsing
// dot) reading e.g. "3 LIVE"; without one it stays the solid red board pill.
export function LivePill({ className = "", count = null }) {
  if (count != null) {
    return (
      <span
        className={`inline-flex items-center gap-1.5 rounded-full bg-[#EF4444]/[0.12] px-2 py-[3px] text-[10px] font-extrabold uppercase tracking-[0.1em] text-[#EF4444] ${className}`}
        style={{ boxShadow: "inset 0 0 0 1px rgba(239,68,68,.35)" }}
      >
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#EF4444]" /> {count} Live
      </span>
    );
  }
  return (
    <span className={`inline-flex items-center gap-1 rounded-md bg-[#EF4444] px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white ${className}`}>
      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" /> Live
    </span>
  );
}

// A neutral "clock / game-state" chip (e.g. "Q3 · 8:42").
export function ClockChip({ children, className = "" }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded-md border border-[#27272A] bg-[#101013] px-2 py-0.5 text-[11px] font-semibold tabular-nums text-[#a1a1aa] ${className}`}>
      {children}
    </span>
  );
}
